import * as React from "react";
import styles from "../../../assets/scss/components/footer/feedbackForm/feedbackForm.module.scss";
import { apiService } from "../../service/apiService";
import { isLoggedIn } from "../../service/login/loginService";
import { integerExp, phoneNo } from "../../utils/regex";
import DeviceContext from "./../../service/indentifyDevice";
import browserName from "./../../utils/browserName";
import deviceName from "./../../utils/deviceName";
import urlConfig from "../../../config/domain";
import bStrg from "./../../service/storageHelper";

export interface IFeedbackFormProps {
  onClose: () => void;
}

type TFieldNames = "name" | "email" | "phone" | "catFeed" | "comments";

type TFieldValues = {
  [x in TFieldNames]: string;
};

type TFieldValidity = {
  [x in TFieldNames]: Partial<ValidityState> & {
    [x: string]: boolean | undefined;
  };
};

type TShowError = {
  [x in TFieldNames]: boolean;
};

type TImageFile = {
  ImgCont: string;
  ImgNm: string;
  Type: string;
};

const FeedbackForm: React.FC<IFeedbackFormProps> = (props) => {
  const [values, setValues] = React.useState<TFieldValues>({
    name: "",
    email: "",
    phone: "",
    catFeed: "Suggestions",
    comments: "",
  });
  const [error, setError] = React.useState<TFieldValidity>({
    name: {},
    email: {},
    phone: { valid: true },
    catFeed: { valid: true },
    comments: {},
  });
  const [showError, setShowError] = React.useState<TShowError>({
    name: false,
    email: false,
    phone: false,
    catFeed: false,
    comments: false,
  });
  const [openOptions, setOpenOptions] = React.useState(false);
  const [files, setFiles] = React.useState<Array<TImageFile>>([]);
  const [fileError, setFileError] = React.useState(false);
  const [imgPrv, setImgPrv] = React.useState("");
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const categories = [
    "Design and Interface",
    "Account Opening",
    "Guided Portfolios Related",
    "Customer Service",
    "Suggestions",
    "Pricing Related",
  ];

  // if the user is logged in then we don't need to check name, email, phone fields for validity
  // so they should be valid by default
  React.useEffect(() => {
    if (isLoggedIn()) {
      setError({
        name: { valid: true },
        email: { valid: true },
        phone: { valid: true },
        catFeed: { valid: true },
        comments: {},
      });
    }
  }, []);

  function submitFeedback(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const isLgdIn = isLoggedIn();

    const body = {
      Brw: browserName().browserName,
      Cmmt: values.comments || "",
      Dev: deviceName(),
      Em:
        (isLgdIn ? bStrg("PersonalDetails").emailAddressField : values.email) ||
        "",
      Imgs: files || [],
      Mob:
        (isLgdIn ? bStrg("PersonalDetails").mobileNumberField : values.phone) ||
        "",
      Mod: "GEN",
      Nm: (isLgdIn ? bStrg("PersonalDetails").nameField : values.name) || "",
      Opt: values.catFeed,
      Pg: "HomePage",
      Ptf: "EWIN",
      Rtg: 0,
      Url: window.location.href,
    };

    apiService(
      { url: urlConfig.feedback + "submit", method: "POST", body },
      () => setIsSubmitted(true)
    );
  }

  function fieldChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    let { name, value, validity } = e.target;

    // stop showing error if it is being shown
    if (showError[name as TFieldNames]) {
      setShowError({ ...showError, [name]: false });
    }

    setValues({ ...values, [name]: value });
    setError({ ...error, [name]: validity });
  }

  function phoneChange(e: React.ChangeEvent<HTMLInputElement>) {
    let { name, value, validity } = e.target;
    const tempValidity: any = validity;

    // stop showing error if it is being shown
    if (showError[name as TFieldNames]) {
      setShowError({ ...showError, [name]: false });
    }

    if (!integerExp.test(value)) {
      // only allow integers in mobile
      value = values.phone;
    }

    setValues({ ...values, [name]: value });

    if (value && !phoneNo.test(value)) {
      e.target.setCustomValidity("phoneNo");
      tempValidity.phoneNo = true;
    } else if (value && +value < 1000000000) {
      e.target.setCustomValidity("minValue");
      tempValidity.minValue = true;
    } else {
      e.target.setCustomValidity("");
    }

    setError({ ...error, [name]: tempValidity });
  }

  function setCategory(cat: string) {
    setValues({ ...values, catFeed: cat });
  }

  function onSelectFile(e: React.ChangeEvent<HTMLInputElement>) {
    uploadFile(e.target.files![0]);
  }

  function viewFile(img: string, e: React.MouseEvent<HTMLElement, MouseEvent>) {
    if ((e.target as HTMLElement).id !== "close_img") setImgPrv(img);
  }

  function hidePreview() {
    setImgPrv("");
  }

  function removeFile(index: number) {
    setFiles(files.filter((_, i) => i !== index));
  }

  function dropHandler(e: React.DragEvent<HTMLElement>) {
    e.preventDefault();

    if (e.dataTransfer.items) {
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        if (e.dataTransfer.items[i].kind === "file") {
          uploadFile(e.dataTransfer.items[i].getAsFile()!);
        }
      }
    }
  }

  function uploadFile(file: File) {
    if (file.size > 1048576 || file.type.split("/")[0] !== "image") {
      // if file size is more than 1MB:
      if (!fileError) setFileError(true);
      return;
    }

    if (fileError) setFileError(false);

    let reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = function () {
      setFiles([
        ...files,
        { ImgCont: reader.result as string, ImgNm: file.name, Type: file.type },
      ]);
    };

    reader.onerror = function () {
      //console.log(reader.error);
    };
  }

  return (
    <>
      <div className={styles.overlay}>
        <div className={styles.popup}>
          {!isSubmitted ? (
            <>
              {/* Header */}
              <header>
                <div className={styles.close_cont}>
                  <button className={styles.close} onClick={props.onClose}>
                    <i className="edel-icon-close" />
                  </button>
                </div>
                <h2 className={styles.heading}>
                  Please provide your feedback below
                </h2>
              </header>

              {/* Main content */}
              <div className={styles.body}>
                <form onSubmit={submitFeedback} noValidate>
                  {!isLoggedIn() && (
                    <fieldset className={styles.row}>
                      <div
                        className={
                          DeviceContext().isDesktop ? styles.left_col : "mg-b20"
                        }
                      >
                        <label htmlFor="name">
                          Name <span>*</span>
                        </label>
                        <input
                          value={values.name}
                          type="text"
                          name="name"
                          id="name"
                          maxLength={100}
                          required
                          onChange={fieldChange}
                          onBlur={() =>
                            setShowError({ ...showError, name: true })
                          }
                        />
                        {showError.name && !error.name.valid && (
                          <span className={styles.errMsg}>
                            Please enter a valid name
                          </span>
                        )}
                      </div>
                      <div
                        className={
                          DeviceContext().isDesktop
                            ? styles.right_col
                            : "mg-b20"
                        }
                      >
                        <label htmlFor="emailID">
                          Email ID <span>*</span>
                        </label>
                        <input
                          value={values.email}
                          type="email"
                          name="email"
                          id="emailID"
                          maxLength={50}
                          required
                          onChange={fieldChange}
                          onBlur={() =>
                            setShowError({ ...showError, email: true })
                          }
                        />
                        {showError.email && !error.email.valid && (
                          <span className={styles.errMsg}>
                            Please enter a valid email id
                          </span>
                        )}
                      </div>
                    </fieldset>
                  )}
                  <fieldset className={styles.row}>
                    {!isLoggedIn() && (
                      <div
                        className={
                          DeviceContext().isDesktop ? styles.left_col : "mg-b20"
                        }
                      >
                        <label htmlFor="phNo">Mobile Number</label>
                        <input
                          value={values.phone}
                          type="tel"
                          id="phNo"
                          name="phone"
                          maxLength={10}
                          onChange={phoneChange}
                          onBlur={() =>
                            setShowError({ ...showError, phone: true })
                          }
                        />
                        {showError.phone && (
                          <span className={styles.errMsg}>
                            {error.phone.phoneNo ? (
                              <span>Please enter a valid mobile number.</span>
                            ) : error.phone.minValue ? (
                              <span>Mobile Number length should be 10.</span>
                            ) : (
                              <></>
                            )}
                          </span>
                        )}
                      </div>
                    )}
                    <div
                      className={
                        DeviceContext().isDesktop ? styles.right_col : "mg-b20"
                      }
                    >
                      <label htmlFor="catFeed">
                        Category of Feedback <span>*</span>
                      </label>
                      <div className={styles.dropdown} id="catFeed">
                        <button
                          className={styles.btn}
                          id="catFeedBtn"
                          type="button"
                          aria-haspopup="true"
                          aria-expanded="false"
                          onFocus={() => {
                            setOpenOptions(true);
                          }}
                          onBlur={() =>
                            setTimeout(() => setOpenOptions(false), 200)
                          }
                        >
                          {values.catFeed}
                          <i
                            className={
                              styles.down_arrow + " edel-icon-arrow-down"
                            }
                          />
                        </button>
                        <ul
                          className={
                            styles.menu + " " + (openOptions ? styles.open : "")
                          }
                          aria-labelledby="catFeed"
                        >
                          {categories.map((cat) => (
                            <li onClick={() => setCategory(cat)} key={cat}>
                              {cat}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </fieldset>
                  <fieldset className={styles.row}>
                    <div
                      className={
                        styles.comments +
                        (DeviceContext().isMobile ? " mg-b20" : "")
                      }
                    >
                      <label htmlFor="comments">
                        Comments <span>*</span>
                      </label>
                      <textarea
                        id="comments"
                        name="comments"
                        maxLength={500}
                        minLength={2}
                        required
                        onChange={fieldChange}
                        onBlur={() =>
                          setShowError({ ...showError, comments: true })
                        }
                      />
                      {showError.comments && (
                        <>
                          {error.comments.valueMissing ? (
                            <span className={styles.errMsg}>
                              Please enter comments
                            </span>
                          ) : error.comments.tooShort ? (
                            <span className={styles.errMsg}>
                              Please enter atleast two characters
                            </span>
                          ) : (
                            <></>
                          )}
                        </>
                      )}
                    </div>
                  </fieldset>
                  <fieldset>
                    {files.length === 0 && !DeviceContext().isMobile ? (
                      <label
                        className={styles.empty_file}
                        onDrop={dropHandler}
                        onDragOver={(e) => e.preventDefault()}
                        onDragEnter={(e) => e.preventDefault()}
                      >
                        <input
                          type="file"
                          onChange={onSelectFile}
                          accept="image/*"
                          onClick={(e) =>
                            ((e.target as HTMLInputElement).value = "")
                          }
                        />
                        <span
                          className={
                            "edel-icon-file-upload " + styles.file_upload
                          }
                        ></span>
                        <span className={styles.text}>
                          Drag & drop your screenshot here or click here to
                          upload
                        </span>
                      </label>
                    ) : (
                      <div className={styles.file_block}>
                        {files.map((file, index) => (
                          <div className={styles.image_cont} key={index}>
                            <div
                              className={styles.imgbtn_grp}
                              onClick={(e) =>
                                DeviceContext().isMobile &&
                                viewFile(file.ImgCont, e)
                              }
                            >
                              {(file.Type === "image/jpeg" ||
                                file.Type === "image/jpg" ||
                                file.Type === "image/png") && (
                                <img src={file.ImgCont} />
                              )}
                              {file.Type !== "image/jpeg" &&
                                file.Type !== "image/jpg" &&
                                file.Type !== "image/png" && (
                                  <span
                                    className={
                                      "edel-icon-excel " + styles.excel_icon
                                    }
                                  ></span>
                                )}
                              {!DeviceContext().isMobile && (
                                <div className={styles.icons}>
                                  <span
                                    className={styles.first}
                                    onClick={() => removeFile(index)}
                                  >
                                    <i
                                      className="edel-icon-trash"
                                      aria-hidden="true"
                                    ></i>
                                  </span>
                                  <span
                                    className={styles.second}
                                    onClick={(e) => viewFile(file.ImgCont, e)}
                                  >
                                    <i
                                      className="edel-icon-search"
                                      aria-hidden="true"
                                    ></i>
                                  </span>
                                </div>
                              )}
                              {DeviceContext().isMobile && (
                                <button
                                  type="button"
                                  className={styles.close}
                                  aria-label="Close"
                                  onClick={() => removeFile(index)}
                                >
                                  <span aria-hidden="true" id="close_img">
                                    &times;
                                  </span>
                                </button>
                              )}
                            </div>
                            <span>{file.ImgNm}</span>
                          </div>
                        ))}
                        {files.length < 3 && (
                          <div
                            className={
                              styles.new_file + " " + styles.image_cont
                            }
                          >
                            <label
                              className={
                                styles.new_file + " " + styles.imgbtn_grp
                              }
                              onDrop={dropHandler}
                              onDragOver={(e) => e.preventDefault()}
                              onDragEnter={(e) => e.preventDefault()}
                            >
                              <input
                                type="file"
                                onChange={onSelectFile}
                                accept="image/*"
                                id="file-upload"
                                onClick={(e) =>
                                  ((e.target as HTMLInputElement).value = "")
                                }
                              />
                              <div
                                className={styles.cross}
                                aria-hidden="true"
                              ></div>
                            </label>

                            <span>&nbsp;</span>
                          </div>
                        )}
                        {files.length < 1 && (
                          <label
                            htmlFor="file-upload"
                            className={styles.file_msg}
                          >
                            Upload Screenshot
                          </label>
                        )}
                      </div>
                    )}
                    {imgPrv && (
                      <div className={styles.prw}>
                        <div className={styles.prw_parent}>
                          <img src={imgPrv} />

                          <button
                            type="button"
                            className={styles.close}
                            aria-label="Close"
                            onClick={hidePreview}
                          >
                            <span aria-hidden="true">&times;</span>
                          </button>
                        </div>
                      </div>
                    )}
                    {fileError && (
                      <span className={styles.errMsg}>
                        File format should be PNG, JPG, JPEG & not more than 1
                        MB.
                      </span>
                    )}
                  </fieldset>
                  <fieldset>
                    <div className={styles.btns}>
                      <button
                        type="submit"
                        className={
                          styles.ed_btn +
                          " mg-r10 " +
                          (Object.keys(error).find(
                            (err) => !error[err as TFieldNames].valid
                          )
                            ? styles.disabled
                            : "")
                        }
                        disabled={
                          !!Object.keys(error).find(
                            (err) => !error[err as TFieldNames].valid
                          )
                        }
                      >
                        Submit
                      </button>
                      <button
                        type="button"
                        className={styles.btn + " " + styles.btn_olink}
                        onClick={props.onClose}
                      >
                        Cancel
                      </button>
                    </div>
                  </fieldset>
                </form>
              </div>
            </>
          ) : (
            <div className={styles.confirm}>
              <div className={styles.close_cont}>
                <button className={styles.close} onClick={props.onClose}>
                  <i className="edel-icon-close" />
                </button>
              </div>

              <i className={"edel-icon-tick " + styles.tick} />
              <h2 className={styles.head}>Thank you for your feedback.</h2>
              <p className={styles.text}>
                We look forward to enhancing your experience with us.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FeedbackForm;
